import { useMemo } from 'react';
import {
  HydratedCallRecord,
  NetworkPerformance,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { CallIntelConstant, CallIntelQueries, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { getWeaveToken } from '@frontend/auth-helpers';
import { callStatusUtils } from '@frontend/call-intel-core';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { getContactInfo } from '../utils/call-insight-panel-util';

type Props = {
  callRecord: HydratedCallRecord;
  isDemoAccount?: boolean;
};

export const useCallInsightPanelData = ({ callRecord, isDemoAccount }: Props) => {
  const { t } = useTranslation('call-insight-panel');
  const alert = useAlert();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const contactInfo = useMemo(() => getContactInfo(callRecord), [callRecord]);
  const weaveToken = getWeaveToken();

  const callRecordingPath = useMemo(
    () =>
      callRecord
        ? !isDemoAccount
          ? `${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${callRecord.channelId}?location_id=${contactInfo.locationId}&token=${weaveToken}`
          : `${appConfig.BACKEND_API}/${CallIntelConstant.baseUrlV2}/demo/call-recording?token=${weaveToken}`
        : '',
    [callRecord, contactInfo.locationId, weaveToken, isDemoAccount]
  );

  const networkPerformance = useMemo(() => {
    const isFeatureEnabled = getFeatureFlagValue('call-quality-monitoring');
    const hasPerformanceIssue = callRecord.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED;
    const isPerformanceGood = callRecord.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_GOOD;
    const shouldDisplay = hasPerformanceIssue || isPerformanceGood;
    return {
      hasPerformanceIssue,
      isFeatureEnabled,
      isPerformanceGood,
      performanceStatus: callRecord.networkPerformance,
      shouldDisplay,
      showQualityAccordion: isFeatureEnabled && shouldDisplay,
    };
  }, [callRecord.networkPerformance, getFeatureFlagValue]);

  const { isFailedCall, isSkippedCall, isProcessingCall } = callStatusUtils.getCallFlags(
    callRecord.callIntelInsights as CallIntelTypes.Call //TODO: Till we remove custom types, we need to cast the insights to CallIntelTypes.Call
  );

  const {
    data: callMetadataRes,
    isLoading: isLoadingCallMetadata,
    refetch: refetchCallMetadata,
  } = CallIntelQueries.useGetCallMetadataQuery({
    request: { id: callRecord.channelId ?? '', locationId: callRecord.locationId ?? '' },
    options: {
      enabled:
        !!callRecord.channelId &&
        !!callRecord.locationId &&
        !!callRecord.callIntelInsights &&
        !isFailedCall &&
        !isSkippedCall &&
        !isProcessingCall,
      onError: () => {
        alert.error(t("Couldn't fetch call metadata. Please try again."));
      },
      staleTime: 1000 * 60 * 5,
    },
  });

  const {
    data: taskDataRes,
    isLoading: isTaskFetching,
    queryKey: taskQueryKey,
  } = CallIntelQueries.useListTasks<CallIntelTypes.ListTaskIO['output']>({
    demoData: null as any,
    isDemoAccount: false,
    payload: {
      locations: callRecord?.locationId ? [callRecord.locationId] : [],
      callIds: callRecord?.channelId ? [callRecord.channelId] : [],
      includeMetaData: true,
    },
    realQueryOptions: {
      cacheTime: 0,
      staleTime: 0,
      enabled: !!callRecord?.channelId && !!callRecord?.locationId,
      onError: () => {
        alert.error(t('Failed to fetch call tasks'));
      },
    },
    demoQueryOptions: {
      enabled: false,
    },
  });

  const callMetadata = useMemo<CallIntelTypes.CallMetadata>(() => {
    const transcript = callMetadataRes?.transcript ?? '';
    return {
      callParticipantsMetadata: {
        caller: callMetadataRes?.callMetadata?.caller ?? '',
        receptionist: callMetadataRes?.callMetadata?.receptionist ?? '',
      },
      rating: callMetadataRes?.rating,
      summary: callMetadataRes?.summary ?? '',
      transcript,
      tasks: (taskDataRes?.tasks ?? []).map<CallIntelTypes.Task>((task) => {
        let parsedMetadata;
        let citation = '';
        if (task.metadata) {
          try {
            parsedMetadata = JSON.parse(task.metadata);
            citation = parsedMetadata.citation || '';
          } catch (error) {
            console.error('Error parsing metadata:', error);
          }
        }
        return {
          ...task,
          citation,
          citationTime: CallIntelUtils.findCitationTime(transcript, citation),
        } as CallIntelTypes.Task;
      }),
    };
  }, [callMetadataRes, taskDataRes]);

  return {
    callMetadata,
    callRecordingPath,
    contactInfo,
    isLoadingCallMetadata,
    isTaskFetching,
    networkPerformance,
    taskQueryKey,
    refetchCallMetadata,
  };
};
